import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { ToastrService } from "ngx-toastr";
import { interval, Observable, switchMap, tap } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import { FormDropdownComponent, FormInputComponent, FormInputTimeComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import {
  DimensionsSyncTypes,
  SynchronizationScheduleEndPoints,
  SynchronizationSchedulePageType,
} from "../../../shared/constants/enums";
import { CronObject, CurrentSchedule, MainResponseModel, ValueText, WfCleanupConfig } from "../../../shared/models";
import { SyncApiService, SystemLayoutService } from "../../../shared/services";
import { createCronExpression, getCronFrequency } from "./sync-schedule.utils";

@Component({
  selector: "adaa-synchronization-schedule",
  templateUrl: "./synchronization-schedule.html",
  styleUrl: "./synchronization-schedule.scss",
  imports: [TranslatePipe, ReactiveFormsModule, FormDropdownComponent, FormInputTimeComponent, FormInputComponent],
})
export class SynchronizationScheduleComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _toastrService = inject(ToastrService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _syncApiService = inject(SyncApiService);

  title = input.required<string>();
  pageType = input.required<SynchronizationSchedulePageType>();

  private readonly _untilDestroy = AdaaHelper.untilDestroyed();

  SynchronizationSchedulePageType = SynchronizationSchedulePageType;

  scheduleForm: FormGroup;

  daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  runSync = signal<boolean>(true);
  frequency = signal<string>("");

  runingJobId = signal<number | undefined>(undefined);
  currentSchedule = signal<CurrentSchedule>({
    frequency: "",
  });
  wfCleanupConfig = signal<WfCleanupConfig>({
    id: 0,
    intervalDays: 0,
    userId: 0,
    comment: "",
    updateTS: 0,
  });
  lastDate = signal<Date | string | undefined>(undefined);
  dimensionType = signal<number | undefined>(1);

  frequencyOptions = signal<ValueText[]>([]);
  months = signal<ValueText[]>([]);
  weeks = signal<ValueText[]>([]);
  dimensionTypes = signal<ValueText[]>([
    { text: this._translateService.instant("dimension.dms_sync"), value: 1 },
    { text: this._translateService.instant("dimension.metric_values_sync"), value: 2 },
  ]);

  isMonthly = computed(() => this.frequency() === "monthly");
  isWeekly = computed(() => this.frequency() === "weekly");
  isInactive = computed(() => this.frequency() === "inactive");
  showTime = computed(() => AdaaHelper.isDefinedAndNotEmpty(this.frequency()) && !this.isInactive());
  isSyncDisabled = computed(() =>
    this.pageType() === SynchronizationSchedulePageType.DIMENSIONS ? !this.runSync() : false
  );
  syncButtonDanger = computed(() => {
    if (this.pageType() == SynchronizationSchedulePageType.WORKFLOW_DECISION) return true;
    return !this.runSync() ? true : false;
  });
  syncButtonLabel = computed(() => {
    if (this.pageType() === SynchronizationSchedulePageType.WORKFLOW_DECISION) return "common.form.button.submit";
    else {
      if (this.runSync()) {
        if (this.pageType() === SynchronizationSchedulePageType.DIMENSIONS) {
          return this.dimensionType() === DimensionsSyncTypes.STRUCTURE
            ? "dimension.start_dms_sync"
            : "dimension.start_metric_values_sync";
        } else {
          return "khadamati.schedule.start_sync";
        }
      } else {
        return "khadamati.schedule.stop_sync";
      }
    }
  });
  showLastSyncDate = computed(() => this.pageType() !== SynchronizationSchedulePageType.WORKFLOW_DECISION);
  endPoint = computed(() => {
    if (this.pageType() === SynchronizationSchedulePageType.DIMENSIONS) {
      return this.dimensionType() === DimensionsSyncTypes.STRUCTURE
        ? SynchronizationScheduleEndPoints.libmetricStructureScheduler
        : SynchronizationScheduleEndPoints.libmetricValueScheduler;
    } else if (this.pageType() === SynchronizationSchedulePageType.ORG_UNIT) {
      return SynchronizationScheduleEndPoints.orgUnit;
    } else {
      return SynchronizationScheduleEndPoints.jobs;
    }
  });
  configuredScheduleLabel = computed(() => {
    const translatedTitle = this._translateService.instant("khadamati.schedule.configuration");

    return this.pageType() !== SynchronizationSchedulePageType.DIMENSIONS
      ? translatedTitle
      : this.dimensionType() === DimensionsSyncTypes.STRUCTURE
        ? `${translatedTitle} - ${this._translateService.instant("dimension.dms_sync")}`
        : `${translatedTitle} - ${this._translateService.instant("dimension.metric_values_sync")}`;
  });

  public ngOnInit(): void {
    this._initPage();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => {
        let redirect = "";
        if (this._router.url.includes("khadamati") || this._router.url.includes("org-unit")) redirect = "/console";
        if (this._router.url.includes("workflow")) redirect = "/console/workflow";

        if (!AdaaHelper.isPMOEntity()) this._router.navigateByUrl(`${redirect}`);
      },
    });
  }

  public saveSyncSchedule() {
    if (this.scheduleForm.invalid) {
      this._toastrService.error(this._translateService.instant("common.form.label.missing_information"));
      return;
    }

    if (this.isInactive()) this._disableScheduler();
    else this._saveScheduler();
  }

  public startStopSync() {
    if (this.pageType() === SynchronizationSchedulePageType.WORKFLOW_DECISION) {
      this._syncApiService.updateWfCleanupConfig(this.wfCleanupConfig()).subscribe({
        next: (response) => this._handleResposeSuccess(response),
      });
    } else this._startStopSyncJob();
  }

  public checkInvalid(control: string) {
    return this.scheduleForm.controls[control]?.invalid ? true : false;
  }

  public getSelectedSchedule(event: number) {
    this.dimensionType.set(event);
    this.scheduleForm.reset({ frequency: "" });
    this._getRunningResponse();

    this._syncApiService.getCurrentScheduler(this.endPoint()).subscribe({
      next: (response) => this._setActiveScheduleForm(response.responseData?.cron ?? ""),
    });
  }

  public setInput(event: number) {
    this.wfCleanupConfig.update((prev) => {
      return {
        ...prev,
        intervalDays: event,
      };
    });
  }

  private _setActiveScheduleForm(cron: string) {
    const schedule = getCronFrequency(cron);
    this.currentSchedule.set(schedule);

    this.scheduleForm.controls["frequency"].setValue(schedule.frequency);
    this.scheduleForm.patchValue({
      dayOfMonth: this.months().find((option) => option.value === schedule.dayOfMonth)?.value,
      dayOfWeek: this.weeks().find((option) => option.value === schedule.dayOfWeek)?.value,
      time: schedule.time,
    });
  }

  private _initPage() {
    this._prepareForm();

    switch (this.pageType()) {
      case SynchronizationSchedulePageType.KHADAMATI:
        this._initKhadamati();
        break;
      case SynchronizationSchedulePageType.WORKFLOW_DECISION:
        this._initWorkflowDecision();
        break;
      case SynchronizationSchedulePageType.ORG_UNIT:
        this._getRunningResponse();
        break;
      case SynchronizationSchedulePageType.DIMENSIONS:
        this._getRunningResponse();
        break;
      default:
        break;
    }

    this._translateService.get(["khadamati.schedule.week.choose"]).subscribe({
      next: () => {
        this._initDDLs();
        this._getCurrentScheduleSync();
      },
    });
  }

  private _initDDLs() {
    //NOTE: Make sure to call this after the translations are loaded
    this._translateService.get("khadamati.schedule.week.choose").subscribe({
      complete: () => {
        this.frequencyOptions.set([
          { text: this._translateService.instant("khadamati.schedule.monthly"), value: "monthly" },
          { text: this._translateService.instant("khadamati.schedule.weekly"), value: "weekly" },
          { text: this._translateService.instant("khadamati.schedule.daily"), value: "daily" },
          { text: this._translateService.instant("khadamati.schedule.inactive"), value: "inactive" },
        ]);

        this.weeks.set(
          this.daysOfWeek.map((day, index) => ({
            value: index + 1,
            text: this._translateService.instant(`khadamati.schedule.week.${day}`),
          }))
        );

        const months: ValueText[] = [];
        for (let index = 1; index <= 31; index++) {
          months.push({
            value: index,
            text: index.toString(),
          });
        }
        this.months.set(months);
      },
    });
  }

  private _prepareForm() {
    this.scheduleForm = this._formBuilder.group({
      frequency: this._formBuilder.control<string | undefined>("", {
        validators: [Validators.required, noWhitespaceValidator()],
      }),
      dayOfMonth: this._formBuilder.control<number | undefined>(undefined),
      dayOfWeek: this._formBuilder.control<number | undefined>(undefined),
      time: this._formBuilder.control<string | undefined>(undefined),
    });

    this.scheduleForm.controls["frequency"].valueChanges.pipe(this._untilDestroy()).subscribe({
      next: (frequency: string) => this._onFrequencyChange(frequency),
    });
  }

  private _onFrequencyChange(frequency: string) {
    if (this.frequency() === frequency) return;
    this.frequency.set(frequency);

    const controls = this.scheduleForm.controls;
    this._toggleRequired(controls["dayOfMonth"], frequency === "monthly");
    this._toggleRequired(controls["dayOfWeek"], frequency === "weekly");
    this._toggleRequired(controls["time"], frequency !== "" && frequency !== "inactive");

    if (frequency !== "monthly") controls["dayOfMonth"].reset(undefined, { emitEvent: false });
    if (frequency !== "weekly") controls["dayOfWeek"].reset(undefined, { emitEvent: false });
    if (frequency === "" || frequency === "inactive") controls["time"].reset(undefined, { emitEvent: false });
  }

  private _toggleRequired(control: AbstractControl, required: boolean) {
    control.setValidators(required ? [Validators.required, noWhitespaceValidator()] : []);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private _getCurrentScheduleSync() {
    this._syncApiService.getCurrentScheduler(this.endPoint()).subscribe({
      next: (response) => this._setActiveScheduleForm(response.responseData?.cron ?? ""),
    });
  }

  private _initKhadamati() {
    this._syncApiService.getKhadamatiLastSyncDate().subscribe({
      next: (response) => this._updateLastRunDate(response.responseData.lastDate),
    });

    interval(30000)
      .pipe(this._untilDestroy())
      .subscribe(() => {
        this._syncApiService.getKhadamatiLastSyncDate().subscribe({
          next: (response) => this._updateLastRunDate(response.responseData.lastDate),
        });
        this._getRunningResponse();
      });
  }

  private _initWorkflowDecision() {
    this._syncApiService.getWfCleanupConfig().subscribe({
      next: (res) => {
        this.wfCleanupConfig.set(res.responseData);
      },
    });
  }

  private _saveScheduler() {
    const formValue = this.scheduleForm.value;
    const hours = formValue?.time.split(":")[0];
    const minutes = formValue?.time.split(":")[1];

    const object: CronObject = {
      hours,
      minutes,
      ...(AdaaHelper.isDefinedAndNotEmpty(formValue.dayOfMonth) ? { dayOfMonth: formValue.dayOfMonth } : {}),
      ...(AdaaHelper.isDefinedAndNotEmpty(formValue.dayOfWeek) ? { dayOfWeek: formValue.dayOfWeek } : {}),
    };
    const cron = createCronExpression(object);

    this._syncApiService.configureScheduler(this.endPoint(), { cron }).subscribe({
      next: (response) => this._handleResposeSuccess(response),
    });
  }

  private _disableScheduler() {
    this._syncApiService.disableScheduler(this.endPoint()).subscribe({
      next: (response) => this._handleResposeSuccess(response),
    });
  }

  private _handleResposeSuccess(response: MainResponseModel<unknown>) {
    if (response.inError) return;
    this._toastrService.success(this._translateService.instant("notification.success.save"));
  }

  private _getRunningResponse() {
    if (this.pageType() === SynchronizationSchedulePageType.WORKFLOW_DECISION) return;

    this._syncApiService.getRunningResponse(this.endPoint()).subscribe({
      next: (response) => this._updateLastRunDate(response.responseData.lastDate),
    });
  }

  private _updateLastRunDate(lastDate: number | undefined | null) {
    if (!lastDate) return;
    this.lastDate.set(moment(new Date(lastDate)).format(Constants.dateTimeWithAmPm));
  }

  private _startStopSyncJob() {
    const url$ = this._getSyncTriggerUrl();
    if (!url$) return;

    url$
      .pipe(
        tap((response) => {
          if (this.pageType() === SynchronizationSchedulePageType.ORG_UNIT)
            this.runingJobId.set(response.responseData as number);

          this.runSync.set(false);
        }),
        switchMap(() => this._syncApiService.getSyncStatus(this.endPoint(), 8)),
        switchMap(async (syncStatus) => {
          const { lastDate } = syncStatus.responseData as { lastDate: number; isRunning: boolean };
          return lastDate ? this._updateLastRunDate(lastDate) : this._getRunningResponse();
        }),
        this._untilDestroy()
      )
      .subscribe({
        next: () => this.runSync.set(true),
      });
  }

  private _getSyncTriggerUrl(): Observable<MainResponseModel<number | undefined>> | null {
    if (this.runSync()) {
      return this._syncApiService.manualTrigger(this.endPoint());
    } else {
      return this._syncApiService.stopSync(this.endPoint(), this.runingJobId());
    }
  }
}
